import { Suspense } from "react";
import { Outlet } from "react-router-dom";
import { PageSkeleton } from "@/components/PageSkeleton";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { SiteManagerSidebar } from "@/components/SiteManagerSidebar";
import { AppHeader } from "@/components/AppHeader";
import { RefreshProvider } from "@/contexts/RefreshContext";
import { SyncProvider } from "@/contexts/SyncContext";
import { BreadcrumbProvider } from "@/contexts/BreadcrumbContext";
import { useAuth } from "@/hooks/useAuth";
import { useIsMobile } from "@/hooks/useMobile";
import { cn } from "@/lib/utils";
import { ConditionalVoiceProvider } from "@/components/layout/ConditionalVoiceProvider";
import { SiteManagerContent } from "@/components/layout/SiteManagerContent";
import { CompanyContent } from "@/components/layout/CompanyContent";
import { SiteManagerDrawerNav } from "@/components/layout/SiteManagerDrawerNav";
import { VoiceAgentFAB } from "@/components/layout/VoiceAgentFAB";
import { CompanyDrawerNav } from "@/components/layout/CompanyDrawerNav";
import { AuthLoadingFallback } from "@/components/layout/AuthLoadingFallback";
import { CommandPalette } from "@/components/CommandPalette";

/**
 * Unified layout component for all authenticated users.
 * Adapts sidebar, header, and features based on user role.
 *
 * Architecture:
 * - Fixed viewport (h-svh) with internal scrolling
 * - Sidebar is fixed, header is fixed
 * - Only the content area scrolls
 */
export function AppLayout() {
  const { user, loading: authLoading } = useAuth();
  const isMobile = useIsMobile();

  const isSiteManager = user?.role === "site_manager";

  return (
    <BreadcrumbProvider>
    <RefreshProvider>
      <SyncProvider>
        <ConditionalVoiceProvider isSiteManager={isSiteManager}>
          <SidebarProvider defaultOpen={!isMobile}>
            {isSiteManager ? <SiteManagerSidebar /> : <AppSidebar />}

            <SidebarInset className="min-w-0 safe-area-top">
              {/* Main content area - the ONLY place that scrolls */}
              <div
                className={cn(
                  "flex-1 min-h-0 overflow-hidden bg-background",
                  !isMobile && "px-sm pt-sm pb-sm",
                )}
              >
                <div
                  className={cn(
                    "h-full bg-content overflow-y-auto no-scrollbar",
                    isMobile
                      ? "pb-5xl"
                      : "rounded-sm border border-border",
                  )}
                >
                  <div className={cn("flex items-center justify-end border-b border-border bg-accent", isMobile ? "px-md py-xs" : "px-xl py-xs")}>
                    <AppHeader />
                  </div>
                  <div className={cn(isMobile ? "px-lg pt-lg pb-lg" : "px-xl pt-xl pb-xl")}>
                    {authLoading ? (
                      <AuthLoadingFallback />
                    ) : (
                      <Suspense fallback={<PageSkeleton variant="dashboard" />}>
                        {isSiteManager ? (
                          <SiteManagerContent>
                            <Outlet />
                          </SiteManagerContent>
                        ) : (
                          <CompanyContent>
                            <Outlet />
                          </CompanyContent>
                        )}
                      </Suspense>
                    )}
                  </div>
                </div>
              </div>

              {/* Mobile drawer nav + voice FAB */}
              {isMobile && isSiteManager && (
                <>
                  <SiteManagerDrawerNav />
                  <VoiceAgentFAB />
                </>
              )}
              {isMobile && !isSiteManager && <CompanyDrawerNav />}
            </SidebarInset>
          </SidebarProvider>
        </ConditionalVoiceProvider>
        <CommandPalette />
      </SyncProvider>
    </RefreshProvider>
    </BreadcrumbProvider>
  );
}

export default AppLayout;
